import AppActions from "../actions/AppActions";
import { FEATURE_MAP } from "../constants/BetaConstants";

/**
 * @fileoverview Feature checks. Checks read `config`,
 * which is complete only after `AppActions.initializeFinished`; until then
 * results recompute per call, afterwards they are memoized.
 */

/** Guards `runFeatureNotifications` so the analytics ping fires at most once per session. */
let notified = false;

/** @type {Object|null} Memoized state, set once `AppActions.initializeFinished` has fired. */
let featureSnapshot = null;

/**
 * Runs every FEATURE_MAP check and returns the names of the active features.
 * @returns {string[]}
 */
const runChecks = () =>
  Object.values(FEATURE_MAP).reduce((result, data) => {
    if (data?.check()) result.push(data.name);
    return result;
  }, []);

/**
 * Returns the list of currently active feature names.
 * @returns {string[]} Active feature names.
 */
export const getActiveFeatures = () => featureSnapshot?.activeFeatures ?? runChecks();

/**
 * True if any active feature is flagged `beta`.
 * @param {string[]} activeFeatures
 * @returns {boolean}
 */
const isUsingBeta = (activeFeatures) =>
  activeFeatures.some((featureName) => !!FEATURE_MAP[featureName]?.beta);

/**
 * The message for the first active beta feature that has one, or null.
 * @param {string[]} activeFeatures
 * @returns {import("react").ReactNode|null}
 */
const getBetaMessage = (activeFeatures) => {
  for (const featureName of activeFeatures) {
    const { beta, message } = FEATURE_MAP[featureName] ?? {};
    if (beta && message) return message;
  }
  return null;
};

/**
 * The shape BetaLabel renders from: the active feature list plus the derived beta flag/message.
 * @returns {{ activeFeatures: string[], isBeta: boolean, betaMessage: (import("react").ReactNode|null) }}
 */
const buildFeatureState = () => {
  const activeFeatures = getActiveFeatures();
  return {
    activeFeatures,
    isBeta: isUsingBeta(activeFeatures),
    betaMessage: getBetaMessage(activeFeatures),
  };
};

/** Stable snapshot used by mounted React consumers. */
export const getFeatureSnapshot = () => {
  featureSnapshot ??= buildFeatureState();
  return featureSnapshot;
};

/**
 * Subscribe to the feature snapshot changing. Delegates to the action that
 * causes the only change; the module's own listener registers first (at
 * import), so the snapshot is refreshed before subscribers read it.
 */
export const subscribeToFeatures = (listener) => AppActions.initializeFinished.listen(listener);

/** Fires each active feature's `notify()` at most once per session. */
export const runFeatureNotifications = () => {
  if (notified) return;
  notified = true;
  getActiveFeatures().forEach((featureName) => {
    FEATURE_MAP[featureName]?.notify();
  });
};

/** Freezes the feature state and fires notifications. */
const onInitializeFinished = () => {
  featureSnapshot = null;
  featureSnapshot = buildFeatureState();
  runFeatureNotifications();
};

// Module-level action -> memoize/notify bridge, replacing FeatureStore's `listenTo(AppActions.initializeFinished, this.update)`.
AppActions.initializeFinished.listen(onInitializeFinished);
